import * as React from "react";
import { ITextFieldProps, TextField } from "@fluentui/react/lib/TextField";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { useId } from "@fluentui/react-hooks";
import { Popover, PopoverSurface } from "@fluentui/react-components";
import { CustomPill } from "./TrackedChangeOperationTypePill";
import { TruncatedText } from "./truncatedText/TruncatedText";
import { DirectionalHint } from "@fluentui/react";

export type AutocompleteTextFieldProps = Omit<ITextFieldProps, "onChange" | "value"> & {
  autocompleteList: string[];
  allowFreeInput?: boolean;
  value?: string;
  onChange?: ITextFieldProps["onChange"];
  dropdownMaxHeight?: string;
  noOptionsText?: string;
  caseSensitive?: boolean;
  openOnFocus?: boolean;
  pillDisplayOnBlur?: boolean;
};

const classes = mergeStyleSets({
  optionRow: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "2px 0",
    selectors: {
      "&:hover": { background: "var(--color-grey)" },
      '&[aria-selected="true"]': { background: "var(--color-grey)" },
    },
  },
  displayShell: {
    width: "100%",
    border: "1px solid var(--color-grey)",
    borderRadius: "2px",
    minHeight: "32px",
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    background: "white",
    cursor: "text",
    font: "inherit",
    color: "inherit",
    appearance: "none",
    WebkitAppearance: "none",
  },
  displayShellDisabled: {
    background: "var(--color-grey)",
    cursor: "not-allowed",
    opacity: 0.8,
  },
  popoverSurface: {
    backgroundColor: "white !important",
    borderRadius: "4px !important",
    padding: ".5rem !important",
    boxShadow: "1px 1px 1px 1px var(--color-grey)",
  },
});

export const AutocompleteTextField: React.FC<AutocompleteTextFieldProps> = ({
  autocompleteList,
  allowFreeInput = true,
  value,
  onChange,
  dropdownMaxHeight = "55vh",
  noOptionsText = "No options",
  caseSensitive = false,
  openOnFocus = true,
  pillDisplayOnBlur = true,
  disabled,
  placeholder,
  label,
  ...textFieldProps
}) => {
  const inputId = useId("auto-txt");
  const listBoxId = useId("auto-listbox");

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);

  const [internalValue, setInternalValue] = React.useState<string>(textFieldProps.defaultValue?.toString() ?? "");
  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : internalValue;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isEditing, setIsEditing] = React.useState<boolean>(true);
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);

  React.useLayoutEffect(() => {
    if (containerRef.current) setAnchorEl(containerRef.current);
  }, []);

  const normalizeQueryString = React.useCallback((s: string) => (caseSensitive ? s : s.toLowerCase()), [caseSensitive]);

  const filteredOptions = React.useMemo(() => {
    const needle = normalizeQueryString(displayValue.trim());
    if (!needle) return autocompleteList;
    return autocompleteList.filter((o) => normalizeQueryString(o).startsWith(needle));
  }, [autocompleteList, displayValue, normalizeQueryString]);

  const commitValue = React.useCallback(
    (newVal: string) => {
      if (!isControlled) setInternalValue(newVal);
      onChange?.({} as any, newVal);
    },
    [isControlled, onChange]
  );

  const selectIndex = React.useCallback(
    (index: number) => {
      const chosen = filteredOptions[index];
      if (chosen === undefined) return;
      commitValue(chosen);
      if (pillDisplayOnBlur) setIsEditing(false);
      setIsOpen(false);
      setActiveIndex(-1);
      requestAnimationFrame(() => inputRef.current?.blur());
    },
    [filteredOptions, commitValue, pillDisplayOnBlur]
  );

  const checkToggleEditing = React.useCallback(() => {
    if (!pillDisplayOnBlur) return;
    if ((displayValue ?? "").trim().length > 0) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [pillDisplayOnBlur, displayValue]);

  const hasQuery = (displayValue ?? "").trim().length >= 1;

  const onInputChange = React.useCallback(
    (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newVal?: string) => {
      const s = newVal ?? "";
      if (!isControlled) setInternalValue(s);
      onChange?.(_ev, s);
      setIsEditing(true);

      if (s.trim().length >= 1) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
        setActiveIndex(-1);
      }
      setActiveIndex(-1);
    },
    [isControlled, onChange]
  );

  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (ev: MouseEvent) => {
      const target: Node = ev.target ?? (null as any);
      const insideInput = !!containerRef.current?.contains(target);
      const insidePopover = !!popoverRef.current?.contains(target);
      if (!insideInput && !insidePopover) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    window.addEventListener("mousedown", handler, { capture: true });
    return () => window.removeEventListener("mousedown", handler, { capture: true });
  }, [isOpen]);

  const handleArrowKey = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>, direction: "down" | "up") => {
      ev.preventDefault();
      if (!isOpen) {
        if (hasQuery) setIsOpen(true);
        return;
      }
      if (direction === "down") {
        setActiveIndex((i) => Math.min((i < 0 ? -1 : i) + 1, Math.max(filteredOptions.length - 1, -1)));
      } else {
        setActiveIndex((i) => Math.max(i - 1, -1));
      }
    },
    [isOpen, hasQuery, filteredOptions]
  );

  const handleEnterKey = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (isOpen) {
        ev.preventDefault();
        if (activeIndex >= 0) {
          selectIndex(activeIndex);
        } else {
          if (allowFreeInput) {
            commitValue(displayValue);
          } else if (filteredOptions.length > 0) {
            commitValue(filteredOptions[0]);
          }
          checkToggleEditing();
          setIsOpen(false);
          setActiveIndex(-1);
        }
      } else if (hasQuery) {
        ev.preventDefault();
        setIsOpen(true);
      } else if (allowFreeInput) {
        checkToggleEditing();
      }
    },
    [
      isOpen,
      activeIndex,
      selectIndex,
      allowFreeInput,
      filteredOptions,
      commitValue,
      displayValue,
      checkToggleEditing,
      hasQuery,
    ]
  );

  const onKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === "ArrowDown") {
        handleArrowKey(ev, "down");
      } else if (ev.key === "ArrowUp") {
        handleArrowKey(ev, "up");
      } else if (ev.key === "Enter") {
        handleEnterKey(ev);
      } else if (ev.key === "Escape" && isOpen) {
        ev.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
      }
    },
    [handleArrowKey, handleEnterKey, isOpen]
  );

  const onFocusInput = React.useCallback(() => {
    setIsEditing(true);
    if (openOnFocus && hasQuery) setIsOpen(true);
  }, [openOnFocus, hasQuery]);

  const onBlurInput = React.useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      requestAnimationFrame(() => {
        const nextFromEvent = ev.relatedTarget ?? null;
        const nextActive = document.activeElement ?? null;
        const next = nextFromEvent ?? nextActive;

        const goingIntoInput = !!containerRef.current?.contains(next);
        const goingIntoPopover = !!popoverRef.current?.contains(next);
        if (goingIntoInput || goingIntoPopover) {
          return;
        }

        if (!allowFreeInput) {
          const exact = autocompleteList.find((o) => normalizeQueryString(o) === normalizeQueryString(displayValue));
          if (!exact) {
            if (filteredOptions.length > 0) {
              commitValue(filteredOptions[0]);
            } else {
              commitValue("");
            }
          }
        }

        checkToggleEditing();
        setIsOpen(false);
        setActiveIndex(-1);
      });
    },
    [
      allowFreeInput,
      autocompleteList,
      displayValue,
      filteredOptions,
      normalizeQueryString,
      commitValue,
      checkToggleEditing,
    ]
  );

  React.useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        if (openOnFocus && hasQuery) setIsOpen(true);
      });
    }
  }, [isEditing, openOnFocus, hasQuery]);

  const isExactInList = React.useMemo(
    () =>
      (displayValue ?? "").trim().length > 0 &&
      autocompleteList.some((o) => normalizeQueryString(o) === normalizeQueryString(displayValue)),
    [autocompleteList, displayValue, normalizeQueryString]
  );

  const resolvedPillColor = isExactInList ? "var(--color-blue)" : "var(--color-green)";

  // Scroll automatico sull'elemento attivo quando cambia activeIndex
  React.useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.getElementById(`${listBoxId}-opt-${activeIndex}`);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, listBoxId]);

  const onDisplayShellKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled) {
        e.preventDefault();
        setIsEditing(true);
      }
    },
    [disabled]
  );

  const onDisplayShellMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      setIsEditing(true);
      e.preventDefault();
    },
    [disabled]
  );

  const onDisplayShellFocus = React.useCallback(() => {
    if (!disabled) setIsEditing(true);
  }, [disabled]);

  const displayShellClassName = `${classes.displayShell} ${disabled ? classes.displayShellDisabled : ""}`;
  const displayShellTabIndex = disabled ? -1 : 0;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {label ? (
        <label htmlFor={inputId} style={{ display: "block", margin: "0 0 4px 0" }}>
          {label}
        </label>
      ) : null}

      {!isEditing && pillDisplayOnBlur ? (
        <button
          type="button"
          className={displayShellClassName}
          aria-label={displayValue ?? placeholder}
          tabIndex={displayShellTabIndex}
          onMouseDown={onDisplayShellMouseDown}
          onFocus={onDisplayShellFocus}
          onKeyDown={onDisplayShellKeyDown}
        >
          {displayValue ? (
            <CustomPill color={resolvedPillColor} style={{ maxWidth: "200px" }}>
              <TruncatedText text={displayValue} tooltipDirectionalHint={DirectionalHint.bottomCenter} />
            </CustomPill>
          ) : (
            <span>{placeholder}</span>
          )}
        </button>
      ) : (
        <TextField
          {...textFieldProps}
          id={inputId}
          disabled={disabled}
          value={displayValue}
          placeholder={placeholder}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onBlur={onBlurInput}
          onFocus={onFocusInput}
          componentRef={(ref) => {
            inputRef.current = (ref as any)?.inputElement ?? null;
          }}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listBoxId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `${listBoxId}-opt-${activeIndex}` : undefined}
        />
      )}

      {isOpen && anchorEl && isEditing && hasQuery && (
        <Popover
          open
          positioning={{
            target: anchorEl,
            position: "below",
            align: "start",
            offset: 0,
          }}
        >
          <PopoverSurface className={classes.popoverSurface} ref={popoverRef}>
            {filteredOptions?.length ? (
              <div
                id={listBoxId}
                role="listbox"
                style={{
                  maxHeight: dropdownMaxHeight,
                  overflowY: "auto",
                }}
              >
                {filteredOptions.map((opt, idx) => (
                  <div // NOSONAR - native <option> cannot contain custom markup
                    key={opt + idx}
                    id={`${listBoxId}-opt-${idx}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    tabIndex={-1}
                    className={classes.optionRow}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectIndex(idx);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <CustomPill color={"var(--color-blue)"} style={{ maxWidth: "200px" }}>
                      <TruncatedText text={opt} tooltipDirectionalHint={DirectionalHint.bottomCenter} />
                    </CustomPill>
                  </div>
                ))}
              </div>
            ) : (
              <span>{noOptionsText}</span>
            )}
          </PopoverSurface>
        </Popover>
      )}
    </div>
  );
};
